/* eslint-disable max-lines */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { TableUtility } from '@utility/dynamic-table';
import { toast } from 'react-toastify';
import { MODULE_NAMES } from '../../../Constants/modules';
import { AuthContext } from '../../../Contexts/AuthContext';
import FormDialog from '../../../Components/Modal/modal';
import UseAxios from '../../../Api/http/http';
import {
  createCloudProvider,
  getCloudProviders,
  updateCloudProvider,
} from '../../../Api/request/cloudProvider';
import ControlledSwitches from '../../../Components/Switch/switch';
import EditActionIcon from '../../../Assets/images/svg/dar_edit.svg';
import alertIcon from '../../../Assets/images/svg/alert_icon.svg';
import { cloudProviderTableConfig } from './cloudProviderTableConfig';
import './cloudProvider.scss';

const EMPTY_FORM = { name: '' };
const EMPTY_PERMISSIONS = [];

const getApiErrorMessage = (response) => (
  response?.data?.response?.data?.message
  || response?.data?.message
  || response?.data?.response?.data?.externalMessage
  || ''
);

const normalizeCloudProviders = (response) => {
  const rows = Array.isArray(response?.data)
    ? response.data
    : response?.data?.data || response?.data?.cloudProviders || [];

  return rows.map((provider) => normalizeCloudProvider(provider));
};

const normalizeCloudProvider = (provider) => ({
  id: provider.cloud_provider_id,
  name: provider.cloud_provider_name || '',
  isActive: Boolean(provider.is_active),
});

const CloudProviderForm = ({ mode, form, error, isSubmitDisabled, onChange, onClose, onSubmit }) => createPortal(
  <div className="cloud-provider-modal-backdrop">
    <form className="cloud-provider-modal" onSubmit={onSubmit}>
      <div className="cloud-provider-modal__header">
        {mode === 'edit' ? 'Edit Cloud Provider' : 'Add Cloud Provider'}
      </div>
      <div className="cloud-provider-modal__body">
        <label htmlFor="cloud-provider-name" className={error ? 'is-error' : ''}>
          Cloud Provider Name<span className="required-asterisk">*</span>
        </label>
        <input
          id="cloud-provider-name"
          name="name"
          type="text"
          placeholder="Enter Cloud Provider Name"
          value={form.name}
          onChange={onChange}
          className={error ? 'is-error' : ''}
          autoComplete="off"
        />
      </div>
      <div className="cloud-provider-modal__footer">
        <button type="button" className="cloud-provider-modal__btn cloud-provider-modal__btn--ghost" onClick={onClose}>
          Cancel
        </button>
        <button
          type="submit"
          className="cloud-provider-modal__btn cloud-provider-modal__btn--primary"
          disabled={isSubmitDisabled}
        >
          {mode === 'edit' ? 'Update' : 'Add'}
        </button>
      </div>
    </form>
  </div>,
  document.body,
);

const CloudProvider = () => {
  const { userProfile } = React.useContext(AuthContext);
  const permission = userProfile?.permissions?.[MODULE_NAMES.CONFIGURATION_CLOUD_PROVIDER];
  const isSuperAdmin = userProfile?.roleDetails?.some(
    (role) => role.roleName === 'Super Admin',
  );
  const normalizedPermissions = useMemo(
    () => (permission || EMPTY_PERMISSIONS).map((action) => String(action).toLowerCase()),
    [permission],
  );
  const hasAccess = useCallback((action) => {
    if (isSuperAdmin) return true;
    const actionAliases = {
      read: ['read', 'view'],
      create: ['create', 'add'],
      update: ['update', 'edit'],
    };
    return actionAliases[action]?.some((alias) => normalizedPermissions.includes(alias)) || false;
  }, [isSuperAdmin, normalizedPermissions]);
  const [providers, setProviders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [dialog, setDialog] = useState({ isOpen: false, mode: 'add', id: null });
  const [toggleProvider, setToggleProvider] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [hasError, setHasError] = useState(false);

  const loadProviders = useCallback(async () => {
    if (!hasAccess('read')) return;
    const response = await UseAxios(getCloudProviders(), setIsLoading);
    const errorMessage = getApiErrorMessage(response);
    if (errorMessage) {
      toast.error(errorMessage);
      return;
    }
    setProviders(normalizeCloudProviders(response));
  }, [hasAccess]);

  useEffect(() => {
    loadProviders();
  }, [loadProviders]);

  const closeForm = useCallback(() => {
    setDialog({ isOpen: false, mode: 'add', id: null });
    setForm(EMPTY_FORM);
    setHasError(false);
  }, []);

  const openAdd = useCallback(() => {
    if (!hasAccess('create')) return;
    setDialog({ isOpen: true, mode: 'add', id: null });
    setForm(EMPTY_FORM);
    setHasError(false);
  }, [hasAccess]);

  const openEdit = useCallback((provider) => {
    if (!hasAccess('update')) return;
    setDialog({ isOpen: true, mode: 'edit', id: provider.id });
    setForm({ name: provider.name });
    setHasError(false);
  }, [hasAccess]);

  const selectedProvider = providers.find((provider) => provider.id === dialog.id);
  const isSubmitDisabled = !form.name.trim()
    || (dialog.mode === 'edit' && form.name.trim() === selectedProvider?.name.trim());

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();
    if (!hasAccess(dialog.mode === 'edit' ? 'update' : 'create')) return;
    const name = form.name.trim();
    if (!name) {
      setHasError(true);
      toast.error('Please enter a cloud provider name.');
      return;
    }

    const response = dialog.mode === 'edit'
      ? await UseAxios(
        updateCloudProvider(dialog.id, name, selectedProvider?.isActive ?? true),
        setIsLoading,
      )
      : await UseAxios(createCloudProvider(name), setIsLoading);
    const errorMessage = getApiErrorMessage(response);
    if (errorMessage) {
      toast.error(errorMessage);
      return;
    }
    toast.success(dialog.mode === 'edit'
      ? 'Cloud provider updated successfully.'
      : 'Cloud provider added successfully.');
    const savedProvider = normalizeCloudProvider(response.data);
    setProviders((currentProviders) => (
      dialog.mode === 'edit'
        ? currentProviders.map((provider) => (
          provider.id === savedProvider.id ? savedProvider : provider
        ))
        : [...currentProviders, savedProvider]
    ));
    closeForm();
  }, [closeForm, dialog.id, dialog.mode, form.name, hasAccess, selectedProvider]);

  const handleToggle = useCallback((provider) => {
    if (!hasAccess('update')) return;
    setToggleProvider(provider);
  }, [hasAccess]);

  const handleCloseToggleDialog = useCallback(() => {
    setToggleProvider(null);
  }, []);

  const handleConfirmToggle = useCallback(async () => {
    if (!toggleProvider || !hasAccess('update')) return;

    const nextStatus = !toggleProvider.isActive;
    const response = await UseAxios(
      updateCloudProvider(toggleProvider.id, toggleProvider.name, nextStatus),
      setIsLoading,
    );
    const errorMessage = getApiErrorMessage(response);
    if (errorMessage) {
      toast.error(errorMessage);
      return;
    }

    setProviders((currentProviders) => currentProviders.map((provider) => (
      provider.id === toggleProvider.id
        ? { ...provider, isActive: nextStatus }
        : provider
    )));
    toast.success(`${toggleProvider.name} is now ${nextStatus ? 'active' : 'inactive'}.`);
    handleCloseToggleDialog();
  }, [handleCloseToggleDialog, hasAccess, toggleProvider]);

  const tableData = useMemo(() => providers.map((provider, index) => ({
    ...provider,
    slNo: index + 1,
    cloudProviderName: provider.name,
    status: provider.isActive ? 'Active' : 'Inactive',
    action: (
      <div className="cloud-provider-actions" onClick={(event) => event.stopPropagation()}>
        <ControlledSwitches
          checked={provider.isActive}
          disabled={!hasAccess('update')}
          switchCss="switchCss"
          handleChange={() => handleToggle(provider)}
        />
        <button
          type="button"
          className="cloud-provider-action-btn"
          title="Edit Cloud Provider"
          aria-label={`Edit ${provider.name}`}
          disabled={!hasAccess('update')}
          onClick={() => openEdit(provider)}
        >
          <img src={EditActionIcon} alt="" />
        </button>
      </div>
    ),
  })), [handleToggle, hasAccess, openEdit, providers]);

  const configuration = useMemo(() => ({
    generalSettings: {
      enableApiFiltering: false,
      showPagination: false,
      showToolBar: true,
      userInfo: {},
      handlers: { handleAddIconClick: openAdd },
      toolBarSettings: {
        showAddNew: hasAccess('create'),
        showFilterOption: false,
        showDownloadExcel: false,
        showDownloadCsv: false,
        showExpandCollapse: false,
        showColumnCustomize: false,
        showHelp: false,
        showSearch: false,
        showSaveView: false,
        showReset: false,
        showToolBarHeading: true,
        toolBarHeading: 'Cloud Provider',
      },
    },
    showToolBar: true,
    tableSettings: {
      size: 'small',
      enableTreeTable: false,
      showExpandCollapse: false,
      tableConfig: cloudProviderTableConfig,
      loaderSkeleton: { rows: 8, bodyCells: 1, columns: cloudProviderTableConfig.length },
      paginationConfig: { page: 1, limit: tableData.length, total: tableData.length, totalPages: 1 },
      tableScrollHeight: 'calc(100vh - 140px)',
      maxFrozenColumns: { desktop: 1, tablet: 1, mobile: 1 },
    },
  }), [hasAccess, openAdd, tableData.length]);

  if (!hasAccess('read')) {
    return <div>You do not have permission to view cloud providers.</div>;
  }

  return (
    <div className="cloud-provider-tu">
      <TableUtility configuration={configuration} data={tableData} loading={isLoading} />
      {dialog.isOpen && (
        <CloudProviderForm
          mode={dialog.mode}
          form={form}
          error={hasError}
          isSubmitDisabled={isSubmitDisabled}
          onChange={(event) => {
            setForm({ name: event.target.value });
            setHasError(false);
          }}
          onClose={closeForm}
          onSubmit={handleSubmit}
        />
      )}
      <FormDialog
        open={Boolean(toggleProvider)}
        title={`${toggleProvider?.isActive ? 'Disable' : 'Enable'} Cloud Provider`}
        dialogCss="dialog-message"
        dialogPaperCss="client-enable-dialog-paper"
        paperprops={{}}
        dialogBodycontent={(
          <div className="dialog-body-text">
            <div className="dialog-alert-icon">
              <img alt="Alert Icon" className="icon-image" src={alertIcon} />
            </div>
            <div className="dialog-message-text">
              {`Are you sure you want to ${toggleProvider?.isActive ? 'disable' : 'enable'} this cloud provider?`}
            </div>
          </div>
        )}
        button1="Cancel"
        cancelButtonId="cancel-cloud-provider-toggle"
        button2={toggleProvider?.isActive ? 'Disable' : 'Enable'}
        submitButtonId="confirm-cloud-provider-toggle"
        button1Action={handleCloseToggleDialog}
        button2Action={handleConfirmToggle}
      />
    </div>
  );
};

export default CloudProvider;
